import { useContext, useEffect, useState } from "react";
import GeneralContext from "../../../context/GeneralContext/GeneralContext";
import api from "../../../services/api";
import { displayMessage } from "../../../helpers/messages";
import SL_Button from "../../../components/buttons/Button";
import { SL_Input } from "../../../components/input/Input";
import { Form, Modal, Skeleton } from "antd";
import { SL_Select } from "../../../components/selects/Select";

// Opções de gênero
const GENDER_OPTIONS = [
	{ value: "male", label: displayMessage("MALE") },
	{ value: "female", label: displayMessage("FEMALE") },
	// { value: "unspecified", label: displayMessage("UNSPECIFIED") },
];

// Opções de frequência semanal
const FREQUENCY_OPTIONS = [
	{ value: 1, label: "1x por semana" },
	{ value: 2, label: "2x por semana" },
	{ value: 3, label: "3x por semana" },
	{ value: 4, label: "4x por semana" },
	{ value: 5, label: "5x por semana" },
	{ value: 6, label: "6x por semana" },
	{ value: 7, label: "7x por semana" },
];

// Opções de dor no corpo
const BODY_PAIN_OPTIONS = [
	{ value: 'none', label: 'Não sinto dores' },
	{ value: 'lower_back', label: 'Na lombar' },
	{ value: 'shoulder', label: 'No ombro' },
	{ value: 'knee', label: 'No joelho' },
];

// Opções de duração do treino (em minutos)
const DURATION_OPTIONS = [
	{ value: 40, label: '40 min' },
	{ value: 60, label: '60 min' },
	{ value: 90, label: '90 min' },
];

export function CreateGymWorkoutModal({ onCloseModal, presetCombination }) {
	const [isLoading, setIsLoading] = useState(false);
	const [form] = Form.useForm();

	const { setShowLoading } = useContext(GeneralContext);

	// Quando aberto a partir de uma combinação (Etapa 2), pré-preenche e
	// trava os campos que definem a combinação — o admin só define nome,
	// ordem e sequência. Isso evita criar treinos em combinações erradas.
	useEffect(() => {
		if (presetCombination) {
			form.setFieldsValue(presetCombination);
		}
	}, [presetCombination, form]);

	const combinationLocked = !!presetCombination;

	async function onFinishForm(values) {
		try {
			setShowLoading(true);
			await api.gymWorkouts.create(values);
			await onCloseModal(true);
		} catch (error) {
			console.error("Erro ao criar treino:", error);
		} finally {
			setShowLoading(false);
		}
	}

	return (
		<Modal
			title={
				<div className="text-xl font-bold text-blue-700 mr-3">
					{displayMessage("ADD_WORKOUT")} - {displayMessage("GYM_WORKOUT")}
				</div>
			}
			open={true}
			onCancel={() => onCloseModal(false)}
			centered={true}
			footer={
				<div className="flex flex-col ss:flex-row gap-2 justify-end">
					<SL_Button
						className="w-full ss:w-[150px] bg-gray-500 border-transparent"
						onClick={() => onCloseModal(false)}
					>
						{displayMessage("CLOSE")}
					</SL_Button>

					<SL_Button
						className="w-full ss:w-[150px] m-0 bg-blue-600"
						onClick={() => form.submit()}
					>
						{displayMessage("END_REGISTER")}
					</SL_Button>
				</div>
			}
			maskClosable={false}
			width={600}
		>
			<Form
				onFinish={onFinishForm}
				layout="vertical"
				form={form}
				className={`flex gap-2 flex-col`}
			>
				{isLoading ? (
					<Skeleton active />
				) : (
					<>
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("WORKOUT_NAME")}:
								</span>
							}
							name="name"
							className={`w-full m-0`}
							required
							rules={[
								{
									required: true,
									message: displayMessage("NAME_MANDATORY"),
								},
							]}
							initialValue={""}
						>
							<SL_Input type="text" className="text-blue-700" />
						</Form.Item>

						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("DESCRIPTION")}:
								</span>
							}
							name="description"
							className={`w-full m-0`}
							initialValue={""}
						>
							<SL_Input
								textarea={true}
								rows={2}
								type="text"
								className="text-blue-700"
							/>
						</Form.Item>

						<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
							<Form.Item
								label={
									<span className="text-blue-700 font-semibold">
										{displayMessage("GENDER")}:
									</span>
								}
								name="gender"
								className={`w-full m-0`}
								required
								rules={[
									{
										required: true,
										message: displayMessage("GENDER_MANDATORY"),
									},
								]}
								initialValue={"male"}
							>
								<SL_Select options={GENDER_OPTIONS} disabled={combinationLocked} />
							</Form.Item>

							<Form.Item
								label={
									<span className="text-blue-700 font-semibold">
										{displayMessage("WEEKLY_FREQUENCY")}:
									</span>
								}
								name="weekly_frequency"
								className={`w-full m-0`}
								required
								rules={[
									{
										required: true,
										message: displayMessage("FREQUENCY_MANDATORY"),
									},
								]}
								initialValue={3}
							>
								<SL_Select options={FREQUENCY_OPTIONS} disabled={combinationLocked} />
							</Form.Item>
						</div>

						<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
							<Form.Item
								label={
									<span className="text-blue-700 font-semibold">
										Dor no corpo:
									</span>
								}
								name="body_pain"
								className={`w-full m-0`}
								initialValue={'none'}
							>
								<SL_Select options={BODY_PAIN_OPTIONS} disabled={combinationLocked} />
							</Form.Item>
						</div>

						<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
							<Form.Item
								label={
									<span className="text-blue-700 font-semibold">
										{displayMessage("DURATION_MINUTES")}:
									</span>
								}
								name="duration_minutes"
								className={`w-full m-0`}
								initialValue={40}
							>
								<SL_Select options={DURATION_OPTIONS} disabled={combinationLocked} />
							</Form.Item>

							<Form.Item
								label={
									<span className="text-blue-700 font-semibold">
										{displayMessage("ORDER_INDEX")}:
									</span>
								}
								name="order_index"
								className={`w-full m-0`}
								initialValue={0}
							>
								<SL_Input type="number" className="text-blue-700" />
							</Form.Item>
						</div>

						<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
							<Form.Item
								label={
									<span className="text-blue-700 font-semibold">
										{displayMessage("SEQUENCE")}:
									</span>
								}
								name="sequence"
								className={`w-full m-0`}
								required
								rules={[
									{ required: true, message: displayMessage("SEQUENCE_MANDATORY") },
								]}
								initialValue={1}
								tooltip={displayMessage("SEQUENCE_HELPER")}
							>
								<SL_Input type="number" className="text-blue-700" />
							</Form.Item>
						</div>
					</>
				)}
			</Form>
		</Modal>
	);
}


